/**
 * Custom Integration Form Fields
 *
 * Structured editors for generated custom integrations.
 *
 * Primary responsibilities:
 * - Render credential/header inputs instead of raw JSON blobs
 * - Render common OpenAPI schema fields as editable controls
 * - Convert structured inputs back into the stored integration shape
 *
 * Dependencies:
 * - Input
 * - TextareaAutosize
 */

import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TextareaAutosize } from '@/components/ui/textarea-autosize'

type UnknownRecord = Record<string, unknown>

export type IntegrationCredentialField = {
  description?: string
  hasSavedValue?: boolean
  headerName?: string
  id: string
  key: string
  label: string
  placeholder?: string
  required: boolean
  secret: boolean
  value: string
  valueTemplate?: string
  vaultSecretId?: string
}

export type HeaderRow = {
  id: string
  name: string
  value: string
}

export type IntegrationGenerationResponse = {
  auth_fields?: unknown
  auth_options?: unknown
  auth_strategy?: unknown
  custom_headers?: Record<string, string>
  description: string
  environment_fields?: unknown
  name: string
  schema: unknown
}

export type IntegrationAuthMetadata = {
  authOptions?: UnknownRecord
  documentationEvidence: string[]
  strategy: string
  uncertaintyFlags: string[]
  verificationInstructions: string[]
}

const GENERATED_INTEGRATION_METADATA_KEY = 'x-losi-generated-integration'
const PLACEHOLDER_PATTERN =
  /\{\{\s*([A-Za-z0-9_]+)\s*\}\}|\$\{\s*([A-Za-z0-9_]+)\s*\}|\{\s*([A-Za-z0-9_]+)\s*\}/g
const PUBLIC_CREDENTIAL_SUFFIXES = [
  'ACCOUNT_ID',
  'API_BASE_URL',
  'BASE_URL',
  'CLIENT_ID',
  'DOMAIN',
  'ENDPOINT',
  'HOST',
  'HOSTNAME',
  'ORIGIN',
  'ROOT_URL',
  'SERVER_URL',
  'SUBDOMAIN',
  'TENANT',
  'TOKEN_URL',
  'URL',
  'WORKSPACE'
]
const SECRET_CREDENTIAL_PATTERN =
  /(api[_\s-]?key|secret|token|password|passcode|private[_\s-]?key|client[_\s-]?secret|access[_\s-]?token|refresh[_\s-]?token|bearer|credential|authorization|auth)/i
const SECRET_HEADER_PATTERN =
  /^(authorization|proxy-authorization|cookie|set-cookie|x-api-key|api-key|x-auth-token|x-access-token)$/i
const SECRET_INPUT_HINT_PATTERN = /(password|secret|token|api[_\s-]?key|bearer|private)/i

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getString(record: UnknownRecord, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key]

    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }

  return undefined
}

function getStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []

  return value.flatMap((item) => (typeof item === 'string' && item.trim() ? [item.trim()] : []))
}

function getAliasedStringArray(record: UnknownRecord, keys: string[]): string[] {
  for (const key of keys) {
    const values = getStringArray(record[key])

    if (values.length > 0) {
      return values
    }
  }

  return []
}

function getBoolean(record: UnknownRecord, keys: string[], fallback: boolean): boolean {
  for (const key of keys) {
    if (typeof record[key] === 'boolean') {
      return record[key]
    }
  }

  return fallback
}

function getOptionalBoolean(record: UnknownRecord, keys: string[]): boolean | undefined {
  for (const key of keys) {
    if (typeof record[key] === 'boolean') {
      return record[key]
    }
  }

  return undefined
}

function toCredentialKey(value: string): string {
  return value
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase()
}

function getHeaderPlaceholderKeys(value: string): string[] {
  const keys = new Set<string>()

  for (const match of value.matchAll(PLACEHOLDER_PATTERN)) {
    const key = match[1] || match[2] || match[3]

    if (key) {
      keys.add(toCredentialKey(key))
    }
  }

  return [...keys]
}

function textLooksSecret(value: string | undefined): boolean {
  return Boolean(value && SECRET_CREDENTIAL_PATTERN.test(value))
}

function textLooksSecretInputHint(value: string | undefined): boolean {
  return Boolean(value && SECRET_INPUT_HINT_PATTERN.test(value))
}

function isPublicCredentialKey(value: string | undefined): boolean {
  if (!value) return false

  const key = toCredentialKey(value)

  return PUBLIC_CREDENTIAL_SUFFIXES.some((suffix) => key === suffix || key.endsWith(`_${suffix}`))
}

function inferCredentialSecret(
  record: UnknownRecord,
  key: string,
  headerName?: string,
  valueTemplate?: string
): boolean {
  if (isPublicCredentialKey(key)) return false

  const explicitSecret = getOptionalBoolean(record, ['secret', 'sensitive'])
  if (typeof explicitSecret === 'boolean') return explicitSecret

  const inputHint = getString(record, ['type', 'inputType', 'input_type', 'format'])
  if (textLooksSecretInputHint(inputHint)) return true

  if (headerName && SECRET_HEADER_PATTERN.test(headerName)) return true

  const fieldTextHints = [
    getString(record, ['description', 'help', 'hint']),
    getString(record, ['label', 'title']),
    getString(record, ['placeholder', 'example'])
  ]

  return (
    textLooksSecret(key) ||
    textLooksSecret(headerName) ||
    textLooksSecret(valueTemplate) ||
    fieldTextHints.some(textLooksSecretInputHint)
  )
}

function inferPlaceholderSecret(headerName: string, key: string, valueTemplate: string): boolean {
  if (isPublicCredentialKey(key)) return false
  if (SECRET_HEADER_PATTERN.test(headerName)) return true

  return textLooksSecret(key) || textLooksSecret(headerName) || textLooksSecret(valueTemplate)
}

function replaceCredentialPlaceholders(
  template: string,
  fields: IntegrationCredentialField[]
): string {
  return template.replace(PLACEHOLDER_PATTERN, (match, braced, env, loose) => {
    const key = toCredentialKey(String(braced || env || loose || ''))
    const field = fields.find((item) => item.key === key)

    return field?.value || match
  })
}

function replacePublicCredentialPlaceholders(
  template: string,
  fields: IntegrationCredentialField[]
): string {
  return template.replace(PLACEHOLDER_PATTERN, (match, braced, env, loose) => {
    const key = toCredentialKey(String(braced || env || loose || ''))
    const field = fields.find((item) => item.key === key)

    if (!field || field.secret) return match

    return field.value.trim() || match
  })
}

function resolvePublicCredentialPlaceholders(
  value: unknown,
  fields: IntegrationCredentialField[]
): unknown {
  if (typeof value === 'string') {
    return replacePublicCredentialPlaceholders(value, fields)
  }

  if (Array.isArray(value)) {
    return value.map((item) => resolvePublicCredentialPlaceholders(item, fields))
  }

  if (!isRecord(value)) return value

  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      key,
      resolvePublicCredentialPlaceholders(entry, fields)
    ])
  )
}

function normalizeGeneratedField(value: unknown, index: number): IntegrationCredentialField | null {
  if (!isRecord(value)) return null

  const rawKey =
    getString(value, ['key', 'env_key', 'name', 'id']) ||
    getString(value, ['header', 'headerName', 'header_name']) ||
    `credential_${index + 1}`
  const key = toCredentialKey(rawKey)
  const headerName = getString(value, ['headerName', 'header_name', 'header'])
  const valueTemplate = getString(value, ['valueTemplate', 'value_template', 'template'])
  const vaultSecretId = getString(value, ['vaultSecretId', 'vault_secret_id', 'secretId', 'secret_id'])
  const hasSavedValue = getBoolean(
    value,
    ['hasSavedValue', 'has_saved_value'],
    Boolean(vaultSecretId)
  )

  return {
    description: getString(value, ['description', 'help', 'hint']),
    hasSavedValue,
    headerName,
    id: `${key}-${index}`,
    key,
    label: getString(value, ['label', 'title']) || key.replace(/_/g, ' '),
    placeholder: getString(value, ['placeholder', 'example']),
    required: getBoolean(value, ['required'], true),
    secret: inferCredentialSecret(value, key, headerName, valueTemplate),
    value: getString(value, ['value']) || '',
    valueTemplate,
    vaultSecretId
  }
}

function normalizeGeneratedFieldList(value: unknown): IntegrationCredentialField[] {
  return Array.isArray(value)
    ? value
        .map((item, index) => normalizeGeneratedField(item, index))
        .filter((item): item is IntegrationCredentialField => item !== null)
    : []
}

export function normalizeHeaderRecord(value: unknown): Record<string, string> {
  if (!isRecord(value)) return {}

  return Object.fromEntries(
    Object.entries(value).flatMap(([key, headerValue]) => {
      return typeof headerValue === 'string' ? [[key, headerValue]] : []
    })
  )
}

export function parseStoredHeaderRecord(value: unknown): Record<string, string> {
  if (typeof value === 'string') {
    try {
      const parsed: unknown = JSON.parse(value)

      return normalizeHeaderRecord(parsed)
    } catch {
      return {}
    }
  }

  return normalizeHeaderRecord(value)
}

export function createCredentialFieldsFromGeneration(
  response: IntegrationGenerationResponse
): IntegrationCredentialField[] {
  const explicitFields = [
    ...normalizeGeneratedFieldList(response.environment_fields),
    ...normalizeGeneratedFieldList(response.auth_fields)
  ]
  const fieldsByKey = new Map(explicitFields.map((field) => [field.key, field]))

  for (const [headerName, headerValue] of Object.entries(
    normalizeHeaderRecord(response.custom_headers)
  )) {
    for (const key of getHeaderPlaceholderKeys(headerValue)) {
      if (fieldsByKey.has(key)) continue

      fieldsByKey.set(key, {
        headerName,
        id: `${key}-${fieldsByKey.size}`,
        key,
        label: key.replace(/_/g, ' '),
        required: true,
        secret: inferPlaceholderSecret(headerName, key, headerValue),
        value: '',
        valueTemplate: headerValue
      })
    }
  }

  return [...fieldsByKey.values()]
}

function extractCredentialValue(template: string | undefined, savedValue: string): string {
  if (!template) return savedValue

  const match = [...template.matchAll(PLACEHOLDER_PATTERN)][0]

  if (!match) return savedValue

  const placeholder = match[0]
  const [prefix, suffix] = template.split(placeholder)

  if (!savedValue.startsWith(prefix || '') || !savedValue.endsWith(suffix || '')) {
    return savedValue
  }

  return savedValue.slice((prefix || '').length, savedValue.length - (suffix || '').length)
}

export function createCredentialFieldsFromSchema(
  schema: string,
  headers: Record<string, string>
): IntegrationCredentialField[] {
  const metadata = getGeneratedMetadataRecord(getSchemaRecord(schema))
  const fields = normalizeGeneratedFieldList(metadata?.environment_fields)

  return fields.map((field) => {
    const savedValue = field.headerName ? headers[field.headerName] : undefined

    return {
      ...field,
      hasSavedValue: Boolean(savedValue || field.hasSavedValue || field.vaultSecretId),
      value: savedValue ? extractCredentialValue(field.valueTemplate, savedValue) : field.value
    }
  })
}

export function getEditableHeaderRows(
  headers: Record<string, string>,
  fields: IntegrationCredentialField[]
): HeaderRow[] {
  const credentialHeaderNames = new Set(
    fields.flatMap((field) => (field.headerName ? [field.headerName] : []))
  )

  return headersToRows(
    Object.fromEntries(Object.entries(headers).filter(([name]) => !credentialHeaderNames.has(name)))
  )
}

export function getMissingRequiredCredentialFields(
  fields: IntegrationCredentialField[]
): IntegrationCredentialField[] {
  return fields.filter((field) => field.required && !field.value.trim() && !field.hasSavedValue)
}

export function headersToRows(headers: Record<string, string>): HeaderRow[] {
  return Object.entries(headers).map(([name, value], index) => ({
    id: `${name || 'header'}-${index}`,
    name,
    value
  }))
}

export function rowsToHeaderRecord(rows: HeaderRow[]): Record<string, string> {
  return Object.fromEntries(
    rows.flatMap((row) => {
      const name = row.name.trim()

      return name ? [[name, row.value]] : []
    })
  )
}

export function resolveHeadersForSave(
  rows: HeaderRow[],
  fields: IntegrationCredentialField[]
): Record<string, string> {
  const headers = rowsToHeaderRecord(rows)

  for (const [name, value] of Object.entries(headers)) {
    headers[name] = replaceCredentialPlaceholders(value, fields)
  }

  for (const field of fields) {
    if (!field.headerName || headers[field.headerName]) continue

    headers[field.headerName] = field.valueTemplate
      ? replaceCredentialPlaceholders(field.valueTemplate, fields)
      : field.value
  }

  return headers
}

export function resolveSchemaForSave(
  schema: unknown,
  fields: IntegrationCredentialField[]
): unknown {
  return resolvePublicCredentialPlaceholders(schema, fields)
}

function getSchemaRecord(schema: string): UnknownRecord | null {
  try {
    const parsed: unknown = JSON.parse(schema)

    return isRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}

function getGeneratedMetadataRecord(schemaRecord: UnknownRecord | null): UnknownRecord | null {
  const metadata = schemaRecord?.[GENERATED_INTEGRATION_METADATA_KEY]

  return isRecord(metadata) ? metadata : null
}

export function normalizeIntegrationAuthMetadata(value: unknown): IntegrationAuthMetadata | null {
  if (!isRecord(value)) return null

  const authOptions = isRecord(value.auth_options)
    ? value.auth_options
    : isRecord(value.authOptions)
      ? value.authOptions
      : undefined
  const strategy =
    getString(value, ['auth_strategy', 'authStrategy', 'strategy']) ||
    (authOptions ? getString(authOptions, ['auth_strategy', 'authStrategy', 'strategy']) : '') ||
    'unknown'
  const documentationEvidence = [
    ...getAliasedStringArray(value, ['documentation_evidence', 'documentationEvidence']),
    ...(authOptions
      ? getAliasedStringArray(authOptions, ['documentation_evidence', 'documentationEvidence'])
      : [])
  ]
  const uncertaintyFlags = [
    ...getAliasedStringArray(value, ['uncertainty_flags', 'uncertaintyFlags']),
    ...(authOptions
      ? getAliasedStringArray(authOptions, ['uncertainty_flags', 'uncertaintyFlags'])
      : [])
  ]
  const verificationInstructions = [
    ...getAliasedStringArray(value, ['verification_instructions', 'verificationInstructions']),
    ...(authOptions
      ? getAliasedStringArray(authOptions, [
          'verification_instructions',
          'verificationInstructions'
        ])
      : [])
  ]

  if (
    strategy === 'unknown' &&
    documentationEvidence.length === 0 &&
    uncertaintyFlags.length === 0 &&
    verificationInstructions.length === 0 &&
    !authOptions
  ) {
    return null
  }

  return {
    authOptions,
    documentationEvidence,
    strategy,
    uncertaintyFlags,
    verificationInstructions
  }
}

export function getIntegrationAuthMetadataFromGeneration(
  response: IntegrationGenerationResponse
): IntegrationAuthMetadata | null {
  return normalizeIntegrationAuthMetadata(response)
}

export function getIntegrationAuthMetadataFromSchema(
  schema: string
): IntegrationAuthMetadata | null {
  return normalizeIntegrationAuthMetadata(getGeneratedMetadataRecord(getSchemaRecord(schema)))
}

function stringifySchema(schema: UnknownRecord): string {
  return JSON.stringify(schema, null, 2)
}

function getFirstServerUrl(schemaRecord: UnknownRecord | null): string {
  const servers = schemaRecord?.servers

  if (!Array.isArray(servers)) return ''

  const firstServer = servers.find(isRecord)

  return typeof firstServer?.url === 'string' ? firstServer.url : ''
}

function getPathEntries(schemaRecord: UnknownRecord | null) {
  const paths = schemaRecord?.paths

  if (!isRecord(paths)) return []

  return Object.entries(paths).flatMap(([path, methods]) => {
    if (!isRecord(methods)) return []

    return Object.entries(methods).flatMap(([method, operation]) => {
      if (!isRecord(operation)) return []

      return [
        {
          method: method.toUpperCase(),
          operationId: typeof operation.operationId === 'string' ? operation.operationId : '',
          path,
          summary: typeof operation.summary === 'string' ? operation.summary : ''
        }
      ]
    })
  })
}

function patchSchema(schema: string, patch: (schemaRecord: UnknownRecord) => void): string {
  const schemaRecord = getSchemaRecord(schema) || {
    info: {},
    openapi: '3.1.0',
    paths: {},
    servers: [{ url: '' }]
  }

  patch(schemaRecord)

  return stringifySchema(schemaRecord)
}

export const CredentialFieldsEditor: FC<{
  fields: IntegrationCredentialField[]
  onChange: (fields: IntegrationCredentialField[]) => void
}> = ({ fields, onChange }) => {
  const { t } = useTranslation()

  if (fields.length === 0) return null

  return (
    <div className="space-y-2">
      <Label>{t('Credentials')}</Label>
      <div className="space-y-2">
        {fields.map((field) => (
          <div key={field.id} className="space-y-1">
            <Input
              type={field.secret ? 'password' : 'text'}
              autoComplete={field.secret ? 'new-password' : 'off'}
              value={field.value}
              placeholder={
                field.hasSavedValue && field.secret
                  ? t('Saved secret - leave blank to keep it')
                  : field.placeholder || field.label
              }
              onChange={(event) => {
                onChange(
                  fields.map((item) =>
                    item.id === field.id ? { ...item, value: event.target.value } : item
                  )
                )
              }}
            />
            <div className="text-xs text-muted-foreground">
              {field.label}
              {field.required ? ` ${t('(required)')}` : ''}
              {field.description ? ` - ${field.description}` : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export const HeaderRowsEditor: FC<{
  rows: HeaderRow[]
  onChange: (rows: HeaderRow[]) => void
}> = ({ rows, onChange }) => {
  const { t } = useTranslation()

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label>{t('Headers')}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            onChange([
              ...rows,
              {
                id: `header-${Date.now()}`,
                name: '',
                value: ''
              }
            ])
          }
        >
          {t('Add header')}
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground">
          {t('No headers required')}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div
              key={row.id}
              className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)_auto] gap-2"
            >
              <Input
                value={row.name}
                placeholder={t('Header name')}
                onChange={(event) => {
                  onChange(
                    rows.map((item) =>
                      item.id === row.id ? { ...item, name: event.target.value } : item
                    )
                  )
                }}
              />
              <Input
                value={row.value}
                placeholder={t('Header value or {{ENV_KEY}}')}
                onChange={(event) => {
                  onChange(
                    rows.map((item) =>
                      item.id === row.id ? { ...item, value: event.target.value } : item
                    )
                  )
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange(rows.filter((item) => item.id !== row.id))}
              >
                {t('Remove')}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function AuthReviewList({ items }: { items: string[] }) {
  if (items.length === 0) return null

  return (
    <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
      {items.slice(0, 4).map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ul>
  )
}

export const AuthMetadataReview: FC<{
  metadata: IntegrationAuthMetadata | null
}> = ({ metadata }) => {
  const { t } = useTranslation()

  if (!metadata) return null

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium">{t('Authentication review')}</div>
          <div className="text-xs text-muted-foreground">
            {t('Strategy and evidence from the generated API research.')}
          </div>
        </div>
        <span className="shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
          {metadata.strategy}
        </span>
      </div>

      {metadata.documentationEvidence.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-medium">{t('Evidence')}</div>
          <AuthReviewList items={metadata.documentationEvidence} />
        </div>
      )}

      {metadata.uncertaintyFlags.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-amber-700">{t('Needs confirmation')}</div>
          <AuthReviewList items={metadata.uncertaintyFlags} />
        </div>
      )}

      {metadata.verificationInstructions.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-medium">{t('Verification')}</div>
          <AuthReviewList items={metadata.verificationInstructions} />
        </div>
      )}
    </div>
  )
}

export const OpenApiSchemaEditor: FC<{
  onSchemaChange: (schema: string) => void
  onSchemaError: (error: string) => void
  schema: string
  schemaError: string
  validateSchema: (schema: unknown) => Promise<void>
}> = ({ onSchemaChange, onSchemaError, schema, schemaError, validateSchema }) => {
  const { t } = useTranslation()
  const schemaRecord = useMemo(() => getSchemaRecord(schema), [schema])
  const info = isRecord(schemaRecord?.info) ? schemaRecord.info : {}
  const endpoints = useMemo(() => getPathEntries(schemaRecord), [schemaRecord])

  const applyPatch = (patch: (schemaRecord: UnknownRecord) => void) => {
    const nextSchema = patchSchema(schema, patch)
    onSchemaChange(nextSchema)
  }

  const updateInfo = (key: string, value: string) => {
    applyPatch((schemaRecord) => {
      const currentInfo = isRecord(schemaRecord.info) ? schemaRecord.info : {}
      schemaRecord.info = {
        ...currentInfo,
        [key]: value
      }
    })
  }

  return (
    <div className="space-y-3">
      <Label>{t('API schema')}</Label>

      <div className="grid gap-2 sm:grid-cols-2">
        <Input
          value={typeof info.title === 'string' ? info.title : ''}
          placeholder={t('API title')}
          onChange={(event) => updateInfo('title', event.target.value)}
        />
        <Input
          value={typeof info.version === 'string' ? info.version : ''}
          placeholder={t('Version')}
          onChange={(event) => updateInfo('version', event.target.value)}
        />
      </div>

      <Input
        value={getFirstServerUrl(schemaRecord)}
        placeholder={t('Base URL')}
        onChange={(event) => {
          applyPatch((schemaRecord) => {
            schemaRecord.servers = [{ url: event.target.value }]
          })
        }}
      />

      <Input
        value={typeof info.description === 'string' ? info.description : ''}
        placeholder={t('API description')}
        onChange={(event) => updateInfo('description', event.target.value)}
      />

      <div className="space-y-2">
        <Label className="text-xs">{t('Endpoints')}</Label>
        {endpoints.length === 0 ? (
          <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground">
            {t('No endpoints in schema yet')}
          </div>
        ) : (
          <div className="space-y-1">
            {endpoints.slice(0, 12).map((endpoint) => (
              <div
                key={`${endpoint.method}-${endpoint.path}-${endpoint.operationId}`}
                className="grid grid-cols-[70px_minmax(0,1fr)] gap-2 rounded-md border px-3 py-2 text-sm"
              >
                <div className="font-mono text-xs text-muted-foreground">{endpoint.method}</div>
                <div className="min-w-0">
                  <div className="truncate font-medium">{endpoint.path}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {endpoint.operationId || endpoint.summary}
                  </div>
                </div>
              </div>
            ))}
            {endpoints.length > 12 && (
              <div className="text-xs text-muted-foreground">
                {t('{{count}} more endpoints in advanced schema', {
                  count: endpoints.length - 12
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <details className="space-y-2">
        <summary className="cursor-pointer text-sm text-muted-foreground">
          {t('Advanced OpenAPI JSON')}
        </summary>
        <TextareaAutosize
          value={schema}
          onValueChange={(value) => {
            onSchemaChange(value)

            try {
              const parsedSchema: unknown = JSON.parse(value)
              validateSchema(parsedSchema)
                .then(() => onSchemaError(''))
                .catch((error: unknown) =>
                  onSchemaError(error instanceof Error ? error.message : t('Invalid schema'))
                )
            } catch {
              onSchemaError(t('Invalid JSON format'))
            }
          }}
          minRows={8}
          maxRows={12}
        />
      </details>

      {schemaError && <div className="text-xs text-red-500">{schemaError}</div>}
    </div>
  )
}
